import React, { useState } from 'react';
import { Card, Space, Tag, Typography, Button, Modal, message as antdMessage } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';
import { formatFullTime, formatTime } from '../../../utils/messageRenderUtils';

import MessageMediaDetails from './MessageMediaDetails/MessageMediaDetails';
import MessageEscalationInfo from './MessageEscalationInfo/MessageEscalationInfo';
import MessageStatusTimeline from './MessageStatusTimeline/MessageStatusTimeline';
import MessageEditHistory from './MessageEditHistory/MessageEditHistory';
import MessageInfoSection from './MessageInfoSection/MessageInfoSection';

const { Text } = Typography;

const MessageDetails = ({ message, onDeleteMessage, onClose }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [deleteLoading, setDeleteLoading] = useState(false);

    if (!message) return null;

    const themeColors = {
        cardBackground: isDarkMode ? '#1f1f1f' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)',
    };

    const handleDeleteMessage = () => {
        Modal.confirm({
            title: t('delete_message'),
            content: t('delete_message_confirmation'),
            icon: <ExclamationCircleOutlined />,
            okText: t('delete'),
            cancelText: t('cancel'),
            okType: 'danger',
            centered: true,
            onOk: async () => {
                if (!onDeleteMessage) return;

                setDeleteLoading(true);
                try {
                    await onDeleteMessage(message.id);
                    antdMessage.success(t('message_deleted_successfully'));
                    if (onClose) {
                        onClose();
                    }
                } catch (error) {
                    antdMessage.error(error.response?.data?.message || t('error_deleting_message'));
                } finally {
                    setDeleteLoading(false);
                }
            }
        });
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Basic Information */}
            <MessageInfoSection title={t('basic_information')}>
                <Space direction="vertical">
                    <Text style={{ color: themeColors.text }}>{t('id')}: {message.id}</Text>
                    <Text style={{ color: themeColors.text }}>{t('sender_type')}: {message.sender_type}</Text>
                    <Text style={{ color: themeColors.text }}>{t('sent_at')}: {formatFullTime(message.sent_at)}</Text>
                    {message.requires_escalation && <Tag color="red">{t('needs_escalation')}</Tag>}
                    {message.is_abuse && <Tag color="red">{t('marked_as_abusive')}</Tag>}
                </Space>
            </MessageInfoSection>

            {/* Error Information */}
            {message.error_type && message.error_details && (
                <MessageInfoSection
                    title={t('error_information')}
                    titleColor={isDarkMode ? 'orange' : '#ff4d4f'}
                >
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text }}>{t('error_type')}: {message.error_type}</Text>
                        <Text style={{ color: themeColors.text }}>{t('error_details')}: {message.error_details}</Text>
                    </Space>
                </MessageInfoSection>
            )}

            {/* Content Sections */}
            <MessageInfoSection title={t('content')} content={message.content} />
            <MessageInfoSection title={t('agent_analysis')} content={message.agent_analysis} />
            <MessageInfoSection title={t('abuse_information')} content={message.abuse_info} />

            {/* Transaction Information */}
            {message.transaction_info && (
                <MessageInfoSection title={t('transaction_information')}>
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text }}>{t('coins_amount')}: {message.transaction_info.coins_amount}</Text>
                        <Text style={{ color: themeColors.text }}>{t('transaction_id')}: {message.transaction_info.transaction_id}</Text>
                    </Space>
                </MessageInfoSection>
            )}

            <MessageEscalationInfo escalation={message.escalation} />

            <MessageMediaDetails medias={message.medias} />

            <MessageStatusTimeline status={message.status} sentAt={message.sent_at} />

            {/* Reactions */}
            {message.reactions?.length > 0 && (
                <MessageInfoSection title={t('reactions')}>
                    <Space direction="vertical">
                        {message.reactions.map((reaction, index) => (
                            <Space key={index}>
                                <Tag>{reaction.emoji}</Tag>
                                <Text style={{ color: themeColors.text }}>{reaction.sender_type}</Text>
                                <Text type="secondary" style={{ color: themeColors.secondaryText }}>{formatTime(reaction.sent_at)}</Text>
                            </Space>
                        ))}
                    </Space>
                </MessageInfoSection>
            )}

            <MessageEditHistory editHistory={message.edit_history} currentContent={message.content} />

            {/* Reply Context */}
            {message.reply_to && (
                <MessageInfoSection title={t('replying_to')}>
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text, whiteSpace: 'pre-wrap' }}>{message.reply_to.content}</Text>
                        <Text type="secondary" style={{ color: themeColors.secondaryText }}>
                            {t('by')} {message.reply_to.sender_type} {t('at')} {formatFullTime(message.reply_to.sent_at)}
                        </Text>
                    </Space>
                </MessageInfoSection>
            )}

            {/* Actions */}
            {onDeleteMessage && (
                <Card title={t('actions')} style={{ background: themeColors.cardBackground }}>
                    <Button
                        type="default"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deleteLoading}
                        onClick={handleDeleteMessage}
                        block
                    >
                        {t('delete_message')}
                    </Button>
                </Card>
            )}
        </Space>
    );
};

export default MessageDetails;


